import { useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import { paramName, readUrlParams, writeUrlParams } from '../utils/urlParams';

/**
 * THE URL-state primitive (KPR-1579): a page's view, read from the address and
 * written back to it, so a refresh, Back, or a pasted link lands on the same
 * view. The spec and what each entry means are `utils/urlParams`.
 *
 *   const { params, has, update } = useUrlParams({
 *     runsRule: { trim: false },
 *     runsPage: { default: 1, kind: 'page' },
 *   });
 *
 * - `params` is read synchronously on every render (a page's first render
 *   already has its view, KPR-997). It keeps its identity while neither the
 *   address nor the spec changes; an allowlist that grows (the tabs a person
 *   has, once their access is known) is read again.
 * - `has(key)` says whether the address names the param at all, for a page that
 *   tells "absent" from "the default" (the items page's `sortChosen`).
 * - `update(patch, { keepDefaults })` writes with `replace: true` — a view
 *   change is not a history entry, so Back still means "leave the page". Blank
 *   values and defaults are DELETED (`writeUrlParams`), params outside the
 *   patch are left alone, and keys outside the spec are written as named
 *   (`update({ view: 'table' })`). Send ONE patch per event: react-router 6
 *   hands each functional update the address of the last render, so a second
 *   `update` in the same handler replaces the first.
 * - `update` keeps ONE identity for the page's lifetime. react-router 6's
 *   `setSearchParams` is a new function whenever the query string changes, and
 *   a writer built on it changed with it: on the items page, selecting an item
 *   (`?item=`) restarted the feed and paged it back down — 22 requests for one
 *   click at item #181.
 *
 * Built on it: `useUrlListParams` (paging), `useListQuery` (`?q=`),
 * `useItemsViewParams` (the items page), `useTabParam` (`?tab=`).
 */
export default function useUrlParams(spec = {}) {
	const [searchParams, setSearchParams] = useSearchParams();

	const setSearchParamsRef = useRef(setSearchParams);
	setSearchParamsRef.current = setSearchParams;
	const specRef = useRef(spec);
	specRef.current = spec;

	// The spec is a literal at most call sites, so its identity changes every
	// render; what it says is what the read depends on.
	const specKey = JSON.stringify(spec);
	const query = searchParams.toString();
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const params = useMemo(() => readUrlParams(searchParams, spec), [query, specKey]);

	const has = useCallback(key => searchParams.has(paramName(specRef.current, key)), [searchParams]);

	const update = useCallback((patch, options) => {
		setSearchParamsRef.current(prev => writeUrlParams(prev, patch, specRef.current, options), { replace: true });
	}, []);

	return { params, has, update, searchParams };
}
